import React from 'react';
import { CheckIcon, InfoIcon, ErrorIcon, WarningIcon, Dismiss } from './icons';

export interface AlertProps {
  /** Type of Alert styles, options:`success`, `info`, `warning`, `error` */
  type?: 'success' | 'info' | 'warning' | 'error';
  /** Whether Alert can be closed */
  closable?: boolean;
  /** Close text to show */
  closeText?: React.ReactNode;
  /** Content of Alert */
  message?: React.ReactNode;
  /** Additional content of Alert */
  description?: React.ReactNode;
  /** Callback when close Alert */
  onClose?: React.MouseEventHandler<HTMLButtonElement>;
  /** Trigger when animation ending of Alert */
  afterClose?: () => void;
  /** Whether to show icon */
  showIcon?: boolean;
  /** https://www.w3.org/TR/2014/REC-html5-20141028/dom.html#aria-role-attribute */
  role?: string;
  style?: React.CSSProperties;
  prefixCls?: string;
  className?: string;
  banner?: boolean;
  icon?: React.ReactNode;
  /** Custome closeIcon */
  closeIcon?: React.ReactNode;
  action?: React.ReactNode;
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

const Alert: React.FC<AlertProps> = (props) => {
  return (
    <div
      className={classNames(
        props.type === 'warning'
          ? `bg-yellow-50`
          : props.type === 'success'
          ? `bg-green-50`
          : props.type === 'error'
          ? `bg-red-50`
          : `bg-blue-50`,
        `rounded-md p-4`
      )}
    >
      <div className="flex">
        <div className="flex-shrink-0">
          {props.type === 'warning' ? (
            <WarningIcon />
          ) : props.type === 'success' ? (
            <CheckIcon />
          ) : props.type === 'error' ? (
            <ErrorIcon />
          ) : (
            <InfoIcon />
          )}
        </div>
        <div className="ml-3">
          <p
            className={classNames(
              props.type === 'warning'
                ? `text-yellow-800`
                : props.type === 'success'
                ? `text-green-800`
                : props.type === 'error'
                ? `text-red-800`
                : `text-blue-800`,
              `text-sm font-medium`
            )}
          >
            {' '}
            {props.message}
          </p>
          <div
            className={classNames(
              props.type === 'warning'
                ? `text-yellow-700`
                : props.type === 'success'
                ? `text-green-700`
                : props.type === 'error'
                ? `text-red-700`
                : `text-blue-700`,
              `mt-2 text-sm`
            )}
          >
            {props.description}
          </div>
        </div>
        <div className="ml-auto pl-3">
          <div className="-mx-1.5 -my-1.5">
            <button
              type="button"
              className={classNames(
                props.type === 'warning'
                  ? `bg-yellow-50 text-yellow-500 hover:bg-yellow-100 focus:ring-offset-yellow-50 focus:ring-yellow-600`
                  : props.type === 'success'
                  ? `bg-green-50 text-green-500 hover:bg-green-100 focus:ring-offset-green-50 focus:ring-green-600`
                  : props.type === 'error'
                  ? `bg-red-50 text-red-500 hover:bg-red-100 focus:ring-offset-red-50 focus:ring-red-600`
                  : `bg-blue-50 text-blue-500 hover:bg-blue-100 focus:ring-offset-blue-50 focus:ring-blue-600`,
                `inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 `
              )}
            >
              <span className="sr-only">Dismiss</span>
              {<Dismiss />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Alert;
